/**
 * EnergyCardSection — Wrapper de la Carte d'identité énergétique dans le dossier prospect.
 *
 * Objectif : ne pas alourdir le chargement du dossier — la carte (avec ses jauges,
 * charts, moteurs de calcul, appels API multiples) est repliée par défaut avec un
 * simple placeholder. Au clic sur "Afficher", le composant complet est monté
 * paresseusement (les données ne sont fetch que quand l'utilisateur en fait la
 * demande explicite).
 *
 * Le choix (montrer/replier) est mémorisé par dossier dans le localStorage, donc
 * la préférence utilisateur persiste au retour sur le même dossier.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Zap, ChevronDown, ChevronUp, ShieldCheck, Gauge, FileText, ThermometerSun } from 'lucide-react';
import EnergyCard from './EnergyCard';

const LS_KEY = 'valnexo:energy-card-expanded';

export default function EnergyCardSection({ prospect }) {
    // État initial : replié par défaut, sauf si l'user avait déjà déplié ce dossier
    const [expanded, setExpanded] = useState(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(LS_KEY) || '{}');
            return !!stored[prospect?.id];
        } catch { return false; }
    });

    // Persistance de la préférence par dossier
    useEffect(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(LS_KEY) || '{}');
            stored[prospect?.id] = expanded;
            // Nettoyage : garder les 20 derniers dossiers explorés max
            const keys = Object.keys(stored);
            if (keys.length > 20) {
                const trimmed = {};
                keys.slice(-20).forEach((k) => { trimmed[k] = stored[k]; });
                localStorage.setItem(LS_KEY, JSON.stringify(trimmed));
            } else {
                localStorage.setItem(LS_KEY, JSON.stringify(stored));
            }
        } catch { /* ignore */ }
    }, [expanded, prospect?.id]);

    return (
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-indigo-50/40" data-testid="energy-card-section">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-sm shrink-0">
                            <Zap className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                                Carte d&apos;identité énergétique
                                <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-[10px]">Décret Tertiaire · BACS · OPERAT</Badge>
                            </CardTitle>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {expanded
                                    ? 'Trajectoire, réglementation, patrimoine technique et PDF client — repliez pour alléger le dossier.'
                                    : 'Synthèse énergétique et réglementaire complète du site. Cliquez pour afficher.'}
                            </p>
                        </div>
                    </div>
                    <Button
                        onClick={() => setExpanded((v) => !v)}
                        size="sm"
                        className={expanded ? '' : 'bg-purple-600 hover:bg-purple-700 text-white'}
                        variant={expanded ? 'outline' : 'default'}
                        data-testid="energy-card-toggle"
                    >
                        {expanded ? (
                            <><ChevronUp className="w-3.5 h-3.5 mr-1" /> Replier</>
                        ) : (
                            <><ChevronDown className="w-3.5 h-3.5 mr-1" /> Afficher</>
                        )}
                    </Button>
                </div>
            </CardHeader>

            {expanded ? (
                <CardContent className="pt-2">
                    {/* Le composant complet n'est monté que lorsque expanded=true → lazy mount
                        (aucun appel API tant que l'user n'a pas cliqué sur "Afficher"). */}
                    <EnergyCard prospect={prospect} />
                </CardContent>
            ) : (
                <CardContent className="pt-1 pb-4">
                    {/* Aperçu compact des thématiques disponibles quand replié */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <PreviewChip icon={Gauge} label="Trajectoire" hint="Objectif -40 % en 2030" />
                        <PreviewChip icon={ShieldCheck} label="Réglementation" hint="EET · Assujettissement" />
                        <PreviewChip icon={Zap} label="BACS" hint="Puissance CVC · Seuils" />
                        <PreviewChip icon={ThermometerSun} label="Patrimoine" hint="Équipements techniques" />
                        <PreviewChip icon={FileText} label="OPERAT" hint="Cref · Cabs · attestations" />
                        <PreviewChip icon={FileText} label="Consommations" hint="Historique par année" />
                        <PreviewChip icon={FileText} label="Score d'opportunité" hint="Potentiel d'optimisation" />
                        <PreviewChip icon={FileText} label="PDF Premium" hint="Génération + email client" />
                    </div>
                </CardContent>
            )}
        </Card>
    );
}

function PreviewChip({ icon: Icon, label, hint }) {
    return (
        <div className="p-2 rounded-lg bg-white/70 border border-purple-100 flex items-start gap-2">
            <Icon className="w-3.5 h-3.5 text-purple-500 shrink-0 mt-0.5" />
            <div className="min-w-0">
                <div className="font-semibold text-[11px] text-slate-800 truncate">{label}</div>
                <div className="text-[10px] text-slate-500 truncate">{hint}</div>
            </div>
        </div>
    );
}
